import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { DetailedStartupCard } from 'TEIA/components/DetailedStartupCard'
import type { IStartup, DateRange, FilterItem } from 'domain/feature/hub/company/Ecosystem/contracts'
import { HubStartupConnectionsTable } from 'domain/feature/hub/company/Ecosystem/connections/table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PostFormat, postProvider, usePost } from 'sharedKernel/api'
import { Pagination } from 'rsuite'
import { showModal } from 'components/popup'
import { ConnectModal } from 'domain/feature/hub/company/Ecosystem/connectModal'
import { showToast } from 'components/toast'

export const HubStartupGetPage = (): JSX.Element => {
    const { startupId } = useParams()
    const location = useLocation() as { state?: { startup?: IStartup; fromSubTab?: number; registerDateRange?: DateRange } }
    const navigate = useNavigate()
    const startup = location.state?.startup
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [searchFilter, setSearchFilter] = useState<string>('')
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [pageSize, setPageSize] = useState<number>(10)
    const developmentStage = [
        { id: 1, label: 'Ainda não realizei vendas do produto' },
        { id: 2, label: 'Já realizei a primeira venda' },
        { id: 3, label: 'Já tenho vendas recorrentes' }
    ]
    const mapped = startup !== undefined
        ? {
            ...startup,
            address: `${startup.address?.lineOne ?? ''} ${startup.address?.number ?? ''} ${startup.address?.lineTwo ?? ''} ${startup.address?.city ?? ''} - CEP: ${startup.address?.zipcode ?? ''}`.trim()
        }
        : undefined
    const buildFilterBy = (): FilterItem[] => {
        const filterBy: FilterItem[] = []
        if (searchFilter.trim() !== '') {
            filterBy.push({ field: 'responsible', condition: searchFilter.trim(), comparison: 'lk' })
        }
        return filterBy
    }

    const connectionMutation = postProvider(
        async () => {
            refetchConnections()
            showToast('success', 'Conexão criada com sucesso!')
        },
        async (error: any) => {
            console.error('Erro ao criar conexão:', error)
            showToast('error', 'Erro ao criar conexão. Tente novamente.')
        }
    )

    const handleConnectionConfirm = (data: any): void => {
        const payload = {
            url: '/v1/hub/company/ecosystem/connection',
            data: {
                Data: {
                    startupId: data.startupId,
                    companyId: data.startupId,
                    source: data.source,
                    responsible: data.responsible,
                    areaInterest: data.areaInterest,
                    description: data.description
                }
            }
        }
        connectionMutation.mutate(payload)
    }

    const request = useMemo<PostFormat>(() => ({
        url: `/v1/hub/company/ecosystem/startups/connection/${startup?.id}`,
        data: {
            filterBy: buildFilterBy(),
            sort: '',
            limit: pageSize,
            page: currentPage,
            totalRows: 0,
            match: ''
        }
    }), [startupId, pageSize, currentPage, searchFilter])

    const { data: connectionData, isFetching: isConnectionsFetching, refetch: refetchConnections } = usePost<any>(request, `startup-connections-${startupId}`)

    useEffect(() => {
        refetchConnections()
    }, [request])
    const handlePageChange = (eventKey: string | number): void => {
        const newPage = Number(eventKey)
        setCurrentPage(newPage)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const handlePrevPage = (): void => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const handleNextPage = (): void => {
        const totalRows = Number(connectionData?.totalRows ?? 0)
        const totalPages = Math.ceil(totalRows / pageSize)
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    return (
        <div className="container py-4">
            {DetailedStartupCard !== null && mapped !== undefined
                ? (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
                            <div
                                role="button"
                                aria-label="Voltar"
                                onClick={() => navigate('/hub/company/ecosystem', { state: { tab: 'startups', subTab: location.state?.fromSubTab ?? 0, registerDateRange: location.state?.registerDateRange } })}
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    flexShrink: 0,
                                    aspectRatio: '1 / 1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#0066B3',
                                    cursor: 'pointer'
                                }}
                            >
                                <FontAwesomeIcon icon={['fas', 'arrow-left']} style={{ width: '32px', height: '32px' }} />
                            </div>
                            <div
                                className="ecosystem-profile-title"
                                style={{
                                    color: '#464646',
                                    fontFamily: 'Typography/Font family/Font Sans Serif',
                                    fontSize: '28px',
                                    fontStyle: 'normal',
                                    fontWeight: 700,
                                    lineHeight: '120%'
                                }}
                            >
                                {startup?.companyName ?? '-'}
                            </div>
                        </div>
                        <DetailedStartupCard
                            customPrimaryColor='#0066B3'
                            defaultCollapsed={false}
                            logo={mapped.profileImage ?? undefined}
                            pitch={mapped.startupInfo.pitch ?? ''}
                            contactName={mapped.companyContactName ?? ''}
                            contactPhone={mapped.companyContactMobile ?? ''}
                            contactEmail={mapped.companyEmailContact ?? ''}
                            CNPJ={mapped.companyCNPJ ?? ''}
                            numberEmployees={mapped.startupInfo.numberEmployees ?? ''}
                            website={mapped.companyWebsite ?? ''}
                            activity={mapped.startupInfo.activity ?? ''}
                            businessModel={mapped.startupInfo.businessModel ?? ''}
                            target={mapped.startupInfo.target ?? ''}
                            technologies={mapped.startupInfo.technologies ?? ''}
                            developmentStage={
                                developmentStage.find(
                                    stage => stage.id === mapped.startupInfo.developmentStage
                                )?.label ?? ''
                            }
                            annualTurnover={mapped.startupInfo.annualTurnover?.toString() ?? ''}
                            investmentCaptured={mapped.startupInfo.amount?.toString() ?? ''}
                            address={mapped.address}
                            registrationDate={
                                startup?.createdAt != null
                                    ? new Date(startup.createdAt).toLocaleDateString('pt-BR')
                                    : undefined
                            }
                        />
                        <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div
                                style={{
                                    color: '#464646',
                                    fontFamily: 'Typography/Font family/Font Sans Serif',
                                    fontSize: '18px',
                                    fontStyle: 'normal',
                                    fontWeight: 700,
                                    lineHeight: '140%'
                                }}
                            >
                                Conexões
                            </div>
                            <button
                                type="button"
                                onClick={() => showModal('connect', {
                                    startupId: startup?.id,
                                    title: 'Conexão',
                                    message: <ConnectModal startupId={startup?.id} />,
                                    onConfirm: handleConnectionConfirm
                                })}
                                style={{
                                    display: 'flex',
                                    height: '32px',
                                    width: '155px',
                                    padding: '0 16px',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '8px',
                                    background: '#0066B3',
                                    color: '#FFFFFF',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 700,
                                    lineHeight: '140%',
                                    cursor: 'pointer',
                                    border: 'none',
                                    borderRadius: '4px'
                                }}
                            >
                                <span style={{ fontSize: '16px', lineHeight: 1 }}>+</span>
                                <span>Nova conexão</span>
                            </button>
                        </div>
                        {(connectionData?.rows ?? []).length > 0
                            ? (
                                <div style={{ marginTop: '32px' }}>
                                    <HubStartupConnectionsTable
                                        items={(connectionData?.rows ?? []).map((row: any) => ({
                                            responsible: row.responsible,
                                            interestArea: row.areaInterest,
                                            metWeb: row.source,
                                            startDate: row.createdAt,
                                            lastUpdate: row.updatedAt
                                        }))}
                                    />
                                    <div className="d-flex justify-content-end align-items-center mt-3">
                                        <div className="d-flex align-items-center">
                                            <button
                                                className="btn btn-link p-0 me-2"
                                                onClick={handlePrevPage}
                                                disabled={currentPage === 1}
                                                style={{
                                                    border: 'none',
                                                    background: 'none',
                                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                                    opacity: currentPage === 1 ? 0.5 : 1,
                                                    color: '#0066B3',
                                                    visibility: 'visible'
                                                }}
                                            >
                                                <i className="bi bi-chevron-left" style={{ fontSize: '1.2rem' }}></i>
                                            </button>
                                            <Pagination
                                                activePage={currentPage}
                                                pages={Math.ceil(Number(connectionData?.totalRows ?? 0) / pageSize)}
                                                maxButtons={3}
                                                ellipsis={true}
                                                boundaryLinks={true}
                                                onSelect={handlePageChange}
                                                size="sm"
                                                total={Number(connectionData?.totalRows ?? 0)}
                                                className="custom-pagination"
                                            />
                                            <button
                                                className="btn btn-link p-0 ms-2"
                                                onClick={handleNextPage}
                                                disabled={currentPage === Math.ceil(Number(connectionData?.totalRows ?? 0) / pageSize)}
                                                style={{
                                                    border: 'none',
                                                    background: 'none',
                                                    cursor: currentPage === Math.ceil(Number(connectionData?.totalRows ?? 0) / pageSize) ? 'not-allowed' : 'pointer',
                                                    opacity: currentPage === Math.ceil(Number(connectionData?.totalRows ?? 0) / pageSize) ? 0.5 : 1,
                                                    color: '#0066B3',
                                                    visibility: 'visible'
                                                }}
                                            >
                                                <i className="bi bi-chevron-right" style={{ fontSize: '1.2rem' }}></i>
                                            </button>
                                        </div>
                                        <div className="ms-3 items-per-page" style={{ color: '#0066B3' }}>
                                            {pageSize} itens por página
                                        </div>
                                    </div>
                                </div>
                            )
                            : (
                                <div style={{ marginTop: '32px', color: '#464646', fontSize: '16px', fontStyle: 'normal', fontWeight: 400, lineHeight: '140%' }}>
                                    Ainda não existem conexões com esta startup
                                </div>
                            )}
                    </>
                )
                : (
                    <>
                        <h1>Startup</h1>
                        <p>ID: {startupId}</p>
                        {startup !== undefined && (
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(startup, null, 2)}</pre>
                        )}
                    </>
                )}
        </div>
    )
}
